<template>
  <section>
    <div
      v-if="musicians.length"
      :class="{ 'border-solid border-b md:border-b-2 border-app-primary-grey--concrete pb-6 mb-6': audioWorks.length || substitute || showIntermission }"
    >
      <h3 class="text-app-primary-1-red--fire-brick text-lg xl:text-2xl leading-none font-semibold mb-4">{{ $t('program-musicians-title') }}</h3>
      <ul class="c-program-musicians text-sm">
        <li v-for="{ musician, instruments, roles } in normalizedMusicians" :key="musician" class="leading-none mb-4">
          <strong class="font-semibold">{{ musician }}</strong>
          <template v-if="instruments.length > 0">{{ ' ' }}{{ instruments.join(', ') }}</template>
          <template v-if="roles.length > 0">{{ ' ' }}({{ roles.join(', ') }})</template>
        </li>
      </ul>
    </div>

    <div v-if="audioWorks.length || substitute" :class="{ 'border-solid border-b md:border-b-2 border-app-primary-grey--concrete pb-6 mb-6': showIntermission }">
      <h3 class="text-app-primary-1-red--fire-brick text-lg xl:text-2xl leading-none font-semibold mb-4">{{ $t('program-works-title') }}</h3>
      <MoleculeAudioWorks v-if="audioWorks.length && !substitute" :audio-works="audioWorks" />
      <div v-if="substitute" class="prose prose--app prose--app--text-sm" v-html="substitute" />
    </div>

    <ul v-if="showIntermission || isAmplifiedConcert">
      <template v-if="showIntermission">
        <li v-for="(item, index) in items" :key="index" class="text-sm" :class="{ 'mb-1': index + 1 === items.length && !isAmplifiedConcert }">
          {{ item.description }}
        </li>
      </template>
      <li v-if="isAmplifiedConcert" class="text-sm">
        {{ amplifiedConcert.description }}
        <a v-if="amplifiedConcert.url" :href="amplifiedConcert.url">{{ amplifiedConcert.label }}</a>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import groupBy from 'lodash/groupBy'
import mapValues from 'lodash/mapValues'
import uniq from 'lodash/uniq'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  audioWorks: { type: Array as PropType<any[]>, required: false, default: () => [] },
  hasIntermission: { type: Boolean, required: false, default: false },
  intermissionDescription: { type: String, required: false, default: '' },
  isAmplifiedConcert: { type: Boolean, required: false, default: false },
  musicians: { type: Array as PropType<any[]>, required: false, default: () => [] },
  showIntermission: { type: Boolean, required: false, default: true },
  substitute: { type: String, required: false, default: '' },
})

const { t } = useI18n()
const items = ref([
  {
    label: t('concert.detail.has-intermission-title'),
    description:
      props.intermissionDescription ||
      (props.hasIntermission ? t('concert.detail.has-intermission') : props.showIntermission ? t('concert.detail.has-no-intermission') : undefined),
  },
])
const amplifiedConcert = {
  description: t('concert.detail.is-amplified-concert'),
  url: t('concert.detail.is-amplified-concert-button-url'),
  label: t('concert.detail.is-amplified-concert-button-label'),
}
const normalizedMusicians = computed(() =>
  mapValues(
    groupBy(props.musicians, (musician) => musician.title),
    (musicians) => ({
      musician: musicians[0].title,
      instruments: uniq(musicians.map((musician) => musician.instrumentDescription).filter((value) => !!value)),
      roles: uniq(musicians.map((musician) => musician.role).filter((value) => !!value)),
    }),
  ),
)
</script>

<style lang="scss" scoped>
.c-program-musicians {
  @screen md {
    column-count: 2;
  }
}
</style>
